import React, { useState } from 'react';
import { Trophy, Gift, Calendar, Clock, Package, ArrowRight, Filter, Search, Check, AlertCircle, Lock, Eye, Download, Share2, X, Image, Film, Camera, Wand2, User, Aperture } from 'lucide-react';
interface HistoryItem {
  id: string;
  title: string;
  description?: string;
  imageUrl?: string;
  type: 'reward' | 'creation' | 'achievement' | 'purchase';
  sourceType: 'ritual' | 'calendar' | 'lootbox' | 'purchase' | 'deep_shift' | 'face_shift' | 'promo';
  sourceName: string;
  date: Date;
  status: 'claimed' | 'expired' | 'pending';
  value?: number;
  valueType?: 'tokens' | 'gems' | 'xp';
  metadata?: any;
}
export const History: React.FC = () => {
  const [filter, setFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<HistoryItem | null>(null);
  const historyItems: HistoryItem[] = [{
    id: 'history-1',
    title: 'AI Creation',
    description: 'Deep Shift transformation',
    imageUrl: 'https://placehold.co/400x300/black/white?text=Deep+Shift+Result',
    type: 'creation',
    sourceType: 'deep_shift',
    sourceName: 'Deep Shift',
    date: new Date('2023-08-15T14:30:00'),
    status: 'claimed',
    metadata: {
      renderType: 'image',
      prompt: 'Transform into a cyberpunk character with neon lights',
      model: 'ShiftTransformer v2.0',
      hdEnabled: true
    }
  }, {
    id: 'history-2',
    title: 'AI Creation',
    description: 'Face Shift swap',
    imageUrl: 'https://placehold.co/400x300/black/white?text=Face+Shift+Result',
    type: 'creation',
    sourceType: 'face_shift',
    sourceName: 'Face Shift',
    date: new Date('2023-08-14T10:15:00'),
    status: 'claimed',
    metadata: {
      renderType: 'image',
      faceName: 'My Face 1',
      model: 'FaceTransformer V2.0',
      hdEnabled: false
    }
  }, {
    id: 'history-3',
    title: 'Weekly Premium Lootbox',
    imageUrl: 'https://placehold.co/200x200/purple/white?text=Premium+Lootbox',
    type: 'reward',
    sourceType: 'ritual',
    sourceName: 'Weekly Rituals',
    date: new Date('2023-08-13T16:45:00'),
    status: 'claimed'
  }, {
    id: 'history-4',
    title: 'LuvTokens',
    description: '50 tokens rewarded',
    type: 'reward',
    sourceType: 'calendar',
    sourceName: 'Daily Calendar',
    date: new Date('2023-08-12T09:30:00'),
    status: 'claimed',
    value: 50,
    valueType: 'tokens'
  }, {
    id: 'history-5',
    title: 'Angel Skin: Summer Vibes',
    imageUrl: 'https://placehold.co/200x200/pink/white?text=Summer+Skin',
    type: 'reward',
    sourceType: 'lootbox',
    sourceName: 'Premium Lootbox',
    date: new Date('2023-08-11T11:20:00'),
    status: 'claimed'
  }];
  const filterOptions = [{
    id: 'all',
    label: 'All'
  }, {
    id: 'rewards',
    label: 'Rewards'
  }, {
    id: 'creations',
    label: 'Creations'
  }, {
    id: 'achievements',
    label: 'Achievements'
  }, {
    id: 'purchases',
    label: 'Purchases'
  }];
  const filteredItems = historyItems.filter(item => {
    if (filter === 'rewards' && item.type !== 'reward') return false;
    if (filter === 'creations' && item.type !== 'creation') return false;
    if (filter === 'achievements' && item.type !== 'achievement') return false;
    if (filter === 'purchases' && item.type !== 'purchase') return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return item.title.toLowerCase().includes(query) || item.description && item.description.toLowerCase().includes(query) || item.sourceName.toLowerCase().includes(query);
    }
    return true;
  }).sort((a, b) => b.date.getTime() - a.date.getTime());
  const formatDate = (date: Date) => {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  const getSourceIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'ritual':
        return <Trophy size={18} className="text-purple-400" data-id="element-23493" />;
      case 'calendar':
        return <Calendar size={18} className="text-blue-400" data-id="element-23494" />;
      case 'lootbox':
        return <Package size={18} className="text-amber-400" data-id="element-23495" />;
      case 'purchase':
        return <Gift size={18} className="text-green-400" data-id="element-23496" />;
      case 'deep_shift':
        return <Aperture size={18} className="text-pink-400" data-id="element-23497" />;
      case 'face_shift':
        return <User size={18} className="text-pink-400" data-id="element-23498" />;
      case 'promo':
        return <Gift size={18} className="text-teal-400" data-id="element-23499" />;
      default:
        return <Trophy size={18} className="text-purple-400" data-id="element-23500" />;
    }
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'reward':
        return <Gift size={18} className="text-purple-400" data-id="element-23501" />;
      case 'creation':
        return <Wand2 size={18} className="text-pink-400" data-id="element-23502" />;
      case 'achievement':
        return <Trophy size={18} className="text-amber-400" data-id="element-23503" />;
      case 'purchase':
        return <Package size={18} className="text-green-400" data-id="element-23504" />;
      default:
        return <Gift size={18} className="text-purple-400" data-id="element-23505" />;
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'claimed':
        return <Check size={16} className="text-green-400" data-id="element-23506" />;
      case 'expired':
        return <X size={16} className="text-red-400" data-id="element-23507" />;
      case 'pending':
        return <Clock size={16} className="text-amber-400" data-id="element-23508" />;
      default:
        return <Check size={16} className="text-green-400" data-id="element-23509" />;
    }
  };
  const handleViewItem = (item: HistoryItem) => {
    setSelectedItem(item);
  };
  const handleCloseView = () => {
    setSelectedItem(null);
  };
  const handleDownload = (item: HistoryItem) => {
    alert(`Downloading ${item.title}`);
  };
  const handleShare = (item: HistoryItem) => {
    alert(`Sharing ${item.title}`);
  };
  return <div className="space-y-4" data-id="element-23510">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between" data-id="element-23511">
        <div className="flex flex-wrap gap-2" data-id="element-23512">
          {filterOptions.map(option => <button key={option.id} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === option.id ? 'bg-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} onClick={() => setFilter(option.id)} data-id="element-23513">
              {option.label}
            </button>)}
        </div>
        <div className="relative w-full sm:w-auto" data-id="element-23514">
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search history..." className="w-full sm:w-64 pl-9 pr-4 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-id="element-23515" />
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" data-id="element-23516" />
        </div>
      </div>
      {filteredItems.length === 0 ? <div className="bg-white dark:bg-gray-800 rounded-xl p-6 text-center shadow-sm" data-id="element-23517">
          <Filter className="mx-auto mb-3 text-gray-400" size={24} data-id="element-23518" />
          <h3 className="font-medium mb-1" data-id="element-23519">No items found</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-23520">
            Try adjusting your filters or search query
          </p>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-23521">
          {filteredItems.map(item => <div key={item.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow" data-id="element-23522">
              {item.imageUrl && <div className="aspect-video relative bg-gray-100 dark:bg-gray-900 overflow-hidden" data-id="element-23523">
                  <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover" data-id="element-23524" />
                  <div className="absolute top-2 right-2 px-2 py-1 bg-black/50 backdrop-blur-sm rounded-md text-white text-xs flex items-center gap-1.5" data-id="element-23525">
                    {item.metadata?.renderType === 'video' ? <Film size={12} data-id="element-23526" /> : <Image size={12} data-id="element-23527" />}
                    <span data-id="element-23528">{item.metadata?.renderType || 'Image'}</span>
                  </div>
                </div>}
              <div className="p-4" data-id="element-23529">
                <div className="flex items-start justify-between mb-2" data-id="element-23530">
                  <div className="flex items-center gap-2" data-id="element-23531">
                    {getTypeIcon(item.type)}
                    <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-23532">
                      {item.title}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-23533">
                    {getStatusIcon(item.status)}
                    <span className="capitalize" data-id="element-23534">{item.status}</span>
                  </div>
                </div>
                {item.description && <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-23535">
                    {item.description}
                  </p>}
                <div className="flex items-center justify-between mt-3" data-id="element-23536">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400" data-id="element-23537">
                    {getSourceIcon(item.sourceType)}
                    <span data-id="element-23538">{item.sourceName}</span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-23539">
                    {formatDate(item.date)}
                  </span>
                </div>
                {item.value && item.valueType && <div className="mt-2 px-2 py-1 bg-purple-50 dark:bg-purple-900/20 rounded-md inline-flex items-center gap-1.5" data-id="element-23540">
                    <span className="text-xs font-medium text-purple-700 dark:text-purple-300" data-id="element-23541">
                      +{item.value}{' '}
                      {item.valueType === 'tokens' ? 'LuvTokens' : item.valueType === 'gems' ? 'DesireGems' : 'XP'}
                    </span>
                  </div>}
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between" data-id="element-23542">
                  <button className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" onClick={() => handleViewItem(item)} data-id="element-23543">
                    View Details
                  </button>
                  {item.type === 'creation' && <div className="flex items-center gap-2" data-id="element-23544">
                      <button className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors" onClick={() => handleDownload(item)} data-id="element-23545">
                        <Download size={14} data-id="element-23546" />
                      </button>
                      <button className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors" onClick={() => handleShare(item)} data-id="element-23547">
                        <Share2 size={14} data-id="element-23548" />
                      </button>
                    </div>}
                </div>
              </div>
            </div>)}
        </div>}
      {selectedItem && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-23549">
          <div className="relative w-full max-w-2xl max-h-[90vh] overflow-auto animate-in zoom-in-90 duration-300 bg-white dark:bg-gray-900 rounded-lg shadow-xl" data-id="element-23550">
            <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900" data-id="element-23551">
              <div className="flex items-center gap-2" data-id="element-23552">
                {getTypeIcon(selectedItem.type)}
                <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-23553">
                  {selectedItem.title}
                </h3>
              </div>
              <button className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors" onClick={handleCloseView} data-id="element-23554">
                <X size={18} data-id="element-23555" />
              </button>
            </div>
            <div className="p-4" data-id="element-23556">
              {selectedItem.imageUrl && <div className="mb-4 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800" data-id="element-23557">
                  <img src={selectedItem.imageUrl} alt={selectedItem.title} className="w-full h-auto" data-id="element-23558" />
                </div>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-23559">
                <div data-id="element-23560">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-23561">
                    Details
                  </h4>
                  <div className="space-y-2" data-id="element-23562">
                    <div className="flex items-center justify-between" data-id="element-23563">
                      <span className="text-sm text-gray-600 dark:text-gray-300" data-id="element-23564">
                        Type
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white capitalize" data-id="element-23565">
                        {selectedItem.type}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-23566">
                      <span className="text-sm text-gray-600 dark:text-gray-300" data-id="element-23567">
                        Source
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-23568">
                        {selectedItem.sourceName}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-23569">
                      <span className="text-sm text-gray-600 dark:text-gray-300" data-id="element-23570">
                        Date
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-23571">
                        {formatDate(selectedItem.date)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-23572">
                      <span className="text-sm text-gray-600 dark:text-gray-300" data-id="element-23573">
                        Status
                      </span>
                      <div className="flex items-center gap-1.5" data-id="element-23574">
                        {getStatusIcon(selectedItem.status)}
                        <span className="text-sm font-medium text-gray-900 dark:text-white capitalize" data-id="element-23575">
                          {selectedItem.status}
                        </span>
                      </div>
                    </div>
                    {selectedItem.value && selectedItem.valueType && <div className="flex items-center justify-between" data-id="element-23576">
                        <span className="text-sm text-gray-600 dark:text-gray-300" data-id="element-23577">
                          Value
                        </span>
                        <span className="text-sm font-medium text-purple-600 dark:text-purple-400" data-id="element-23578">
                          +{selectedItem.value}{' '}
                          {selectedItem.valueType === 'tokens' ? 'LuvTokens' : selectedItem.valueType === 'gems' ? 'DesireGems' : 'XP'}
                        </span>
                      </div>}
                  </div>
                </div>
                {selectedItem.type === 'creation' && selectedItem.metadata && <div data-id="element-23579">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-23580">
                      Creation Info
                    </h4>
                    <div className="space-y-2" data-id="element-23581">
                      {selectedItem.metadata.model && <div className="flex items-center justify-between" data-id="element-23582">
                          <span className="text-sm text-gray-600 dark:text-gray-300" data-id="element-23583">
                            Model
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-23584">
                            {selectedItem.metadata.model}
                          </span>
                        </div>}
                      {selectedItem.metadata.renderType && <div className="flex items-center justify-between" data-id="element-23585">
                          <span className="text-sm text-gray-600 dark:text-gray-300" data-id="element-23586">
                            Type
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white capitalize" data-id="element-23587">
                            {selectedItem.metadata.renderType}
                          </span>
                        </div>}
                      {selectedItem.metadata.hdEnabled !== undefined && <div className="flex items-center justify-between" data-id="element-23588">
                          <span className="text-sm text-gray-600 dark:text-gray-300" data-id="element-23589">
                            HD Quality
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-23590">
                            {selectedItem.metadata.hdEnabled ? 'Yes' : 'No'}
                          </span>
                        </div>}
                      {selectedItem.metadata.faceName && <div className="flex items-center justify-between" data-id="element-23591">
                          <span className="text-sm text-gray-600 dark:text-gray-300" data-id="element-23592">
                            Face Used
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-23593">
                            {selectedItem.metadata.faceName}
                          </span>
                        </div>}
                    </div>
                  </div>}
              </div>
              {selectedItem.metadata?.prompt && <div className="mb-4" data-id="element-23594">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-23595">
                    Prompt
                  </h4>
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm text-gray-700 dark:text-gray-300" data-id="element-23596">
                    {selectedItem.metadata.prompt}
                  </div>
                </div>}
              {selectedItem.type === 'creation' && <div className="flex items-center justify-end gap-3 mt-4" data-id="element-23597">
                  <button className="px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-sm font-medium text-gray-900 dark:text-white transition-colors flex items-center gap-2" onClick={() => handleShare(selectedItem)} data-id="element-23598">
                    <Share2 size={16} data-id="element-23599" />
                    <span data-id="element-23600">Share</span>
                  </button>
                  <button className="px-4 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-sm font-medium text-white transition-colors flex items-center gap-2" onClick={() => handleDownload(selectedItem)} data-id="element-23601">
                    <Download size={16} data-id="element-23602" />
                    <span data-id="element-23603">Download</span>
                  </button>
                </div>}
            </div>
          </div>
        </div>}
    </div>;
};